import React, { useMemo } from 'react'

import { Div } from '@vividseats/vivid-ui-kit'

import SubCategoryLayout from '@/components/layouts/sub-category-layout'
import {
    OTHER_THEATER_CATEGORY_LINKS,
    THEATER_TICKETS_BY_CITY_LINKS,
} from '@/components/pages/theater/sub-category/constants'
import LinkWidget from '@/components/shared/link-widget'
import PerformerLinks from '@/components/shared/performer-links'
import SortedPerformerLinks from '@/components/shared/performer-links/sorted-performer-links'
import CmsContent from '@/components/shared/sub-category/components/cms-content'
import OtherCategories from '@/components/shared/sub-category/components/other-categories'
import type { SubCategoryPageProps } from '@/components/shared/sub-category/types'
import { PATHS } from '@/constants'
import { PerformerProvider } from '@/context/performer'

import BroadwayCards from './broadway/broadway-cards'

const TheaterSubCategoryContent: React.FC<SubCategoryPageProps> = ({
    initialSubCategoryPerformersParams,
    initialSubCategoryPerformersData,
    initialSubCategory,
    cardWithImageGroupsData,
    performersDataByLinkGroups,
}) => {
    const sortedLinkGroups: React.ReactNode = useMemo(
        () =>
            initialSubCategory?.webPath === PATHS.BROADWAY ? (
                <div data-testid="featured-events">
                    {performersDataByLinkGroups.map(({ title, links }) => {
                        const curatedLinks = links.map(({ label: name, ...rest }) => ({
                            name,
                            ...rest,
                        }))

                        return (
                            <Div marginBottom={10} key={title} data-testid="sorted-links__container">
                                <PerformerLinks title={title} variant="LinkBank" links={curatedLinks} />
                            </Div>
                        )
                    })}
                </div>
            ) : (
                <PerformerProvider
                    initialParams={initialSubCategoryPerformersParams}
                    initialProps={
                        initialSubCategoryPerformersData ? { data: initialSubCategoryPerformersData } : undefined
                    }
                >
                    <SortedPerformerLinks title="Featured Events" variant="LinkBank" />
                </PerformerProvider>
            ),
        [
            initialSubCategory,
            performersDataByLinkGroups,
            initialSubCategoryPerformersParams,
            initialSubCategoryPerformersData,
        ],
    )

    const mainContent: React.ReactNode = useMemo(
        () => (
            <>
                <div data-testid="featured-events">{sortedLinkGroups}</div>
                <Div marginTop={11} data-testid="cms-content">
                    <CmsContent />
                </Div>
            </>
        ),
        [sortedLinkGroups],
    )

    const rightRailContent: React.ReactNode = useMemo(
        () => (
            <>
                <Div data-testid="other-theater-categories">
                    <OtherCategories title="Other Theater Categories" links={OTHER_THEATER_CATEGORY_LINKS} />
                </Div>
                <Div marginTop={11} data-testid="theater-tickets-by-city">
                    <LinkWidget title="Theater Tickets By City" links={THEATER_TICKETS_BY_CITY_LINKS} />
                </Div>
            </>
        ),
        [],
    )
    const externalImageTransformation = 't_production_card_image'
    const externalImage =
        initialSubCategory?.externalImageFilePath &&
        `${process.env.NEXT_PUBLIC_EXTERNAL_IMAGE_URL_PREFIX}/${externalImageTransformation}${initialSubCategory.externalImageFilePath}`

    const categoryImage = externalImage || initialSubCategory?.mobileImageUrl
    const topContent: React.ReactNode = useMemo(
        () =>
            initialSubCategory?.webPath === PATHS.BROADWAY ? (
                <BroadwayCards cardWithImageGroups={cardWithImageGroupsData} categoryImage={categoryImage} />
            ) : null,
        [initialSubCategory, categoryImage, cardWithImageGroupsData],
    )

    return <SubCategoryLayout topContent={topContent} mainContent={mainContent} rightRailContent={rightRailContent} />
}

export default TheaterSubCategoryContent
